import { useState, useEffect, useCallback } from "react";

import { getInfoWindowHTML } from "../InfoWindowV2";
import {
  calculateRegistrationText,
  calculateMapCenter,
} from "../utils/mapHelpers";
import { ModelGeoCampaign } from "@models/ModelGeoCampaign";

interface UseInfoWindowProps {
  mapRef: any;
  pathname: string;
  S3_URL: string;
  themeClass: any;
}

export const useInfoWindow = ({
  mapRef,
  pathname,
  S3_URL,
  themeClass,
}: UseInfoWindowProps) => {
  const [infoWindow, setInfoWindow] = useState<any>(null);
  const [currentMarker, setCurrentMarker] = useState<ModelGeoCampaign | null>(
    null
  );

  useEffect(() => {
    // Only create InfoWindow when map is ready
    if (
      mapRef.current &&
      typeof window !== "undefined" &&
      window.naver?.maps &&
      !infoWindow
    ) {
      const newInfoWindow = new window.naver.maps.InfoWindow({
        content: "",
        borderWidth: 0,
        backgroundColor: "transparent",
        disableAnchor: false,
        pixelOffset: new window.naver.maps.Point(0, 0),
      });
      setInfoWindow(newInfoWindow);
    }
  }, [mapRef, infoWindow]); // Use mapRef (stable reference) instead of mapRef.current

  const showInfoWindow = useCallback(
    (marker: ModelGeoCampaign &
      {originalLatitude?: ModelGeoCampaign['latitude'],
        originalLongitude?: ModelGeoCampaign['longitude']}) => {
      // Create InfoWindow if it doesn't exist yet
      let currentInfoWindow = infoWindow;
      if (!currentInfoWindow && mapRef.current && window.naver?.maps) {
        currentInfoWindow = new window.naver.maps.InfoWindow({
          content: "",
          borderWidth: 0,
          backgroundColor: "transparent",
          disableAnchor: false,
          pixelOffset: new window.naver.maps.Point(0, 0),
        });
        setInfoWindow(currentInfoWindow);
      }

      if (!currentInfoWindow || !mapRef.current) {
        return;
      }

      const position = new window.naver.maps.LatLng(
        Number(marker.originalLatitude || marker.latitude),
        Number(marker.originalLongitude || marker.longitude),
      );

      const registrationText = calculateRegistrationText(
        marker.registration_end_date!
      );

      const content = getInfoWindowHTML({
        marker,
        S3_URL,
        registrationText,
        themeClass,
        variant: "detail",
      });

      // InfoWindow 높이에 따라 지도 중심 조정
      const infoWindowHeight = pathname.includes("detailPage") ? 400 : 300;
      const newCenter = calculateMapCenter(
        mapRef.current,
        position,
        infoWindowHeight
      );

      // 부드럽게 이동
      mapRef.current.panTo(newCenter, {
        duration: 300,
        easing: "easeOutCubic",
      });

      // InfoWindow 표시
      setTimeout(() => {
        currentInfoWindow.setContent(content);
        currentInfoWindow.open(mapRef.current, position, {
          pixelOffset: new window.naver.maps.Point(-2, -24),
          anchorSkew: false,
          anchorColor: "#ffffff",
          borderWidth: 0,
        });

        const closeBtn = document.getElementById("close-info-window");
        if (closeBtn) {
          closeBtn.addEventListener("click", () => {
            currentInfoWindow.close();
            setCurrentMarker(null);
          });
        }
      }, 350);
    },
    [infoWindow, mapRef, pathname, S3_URL, themeClass]
  );

  return {
    infoWindow,
    currentMarker,
    setCurrentMarker,
    showInfoWindow,
  };
};
